import {engineContext} from './context.js';

(function(global){'use strict';
const VERSION='0.1.0-alpha';

function stableValue(x){
  if(Array.isArray(x)) return x.map(stableValue);
  if(x && typeof x==='object'){
    const out={};
    Object.keys(x).sort().forEach(k=>{out[k]=stableValue(x[k]);});
    return out;
  }
  return x;
}

function canonical(state){
  return JSON.stringify(stableValue(state||{}));
}

function hashString(str){
  let h=2166136261;
  for(let i=0;i<str.length;i++){
    h^=str.charCodeAt(i);
    h=Math.imul(h,16777619);
  }
  return ('00000000'+(h>>>0).toString(16)).slice(-8);
}

function fingerprint(state){
  return hashString(canonical(state));
}

function snapshot(input){
  input=input||{};
  const state={
    symbol:input.symbol||null,
    scope:input.scope||'RESEARCH',
    business:input.business||null,
    stock:input.stock||null,
    timing:input.timing||null,
    whyWrong:input.whyWrong||null,
    readiness:input.readiness||null,
    evidenceFingerprint:input.evidenceFingerprint||null,
    thesisFingerprint:input.thesisFingerprint||null,
    compoundSignal:input.compoundSignal||null,
    invalidatedConclusions:Array.isArray(input.invalidatedConclusions)?[...input.invalidatedConclusions].sort():[],
    openGaps:Array.isArray(input.openGaps)?[...input.openGaps].sort():[],
    nextTask:input.nextTask||null
  };
  return Object.freeze({
    version:VERSION,
    state:Object.freeze(state),
    fingerprint:fingerprint(state)
  });
}

function diff(previous,current){
  const p=previous&&previous.state?previous:snapshot(previous||{});
  const c=current&&current.state?current:snapshot(current||{});
  const changes=[];
  const keys=['scope','business','stock','timing','whyWrong','readiness','evidenceFingerprint','thesisFingerprint','compoundSignal','nextTask'];
  for(const k of keys){
    if(canonical(p.state[k])!==canonical(c.state[k])) changes.push(k);
  }
  if(canonical(p.state.invalidatedConclusions)!==canonical(c.state.invalidatedConclusions)) changes.push('invalidatedConclusions');
  if(canonical(p.state.openGaps)!==canonical(c.state.openGaps)) changes.push('openGaps');

  return Object.freeze({
    same:p.fingerprint===c.fingerprint,
    previousFingerprint:p.fingerprint,
    currentFingerprint:c.fingerprint,
    changes:Object.freeze(changes),
    material:changes.length>0
  });
}

function selfTest(){
  const t=[],ok=(n,p)=>t.push({name:n,pass:!!p});
  const a={
    symbol:'AAA',scope:'OWNED',business:'STRONG',stock:'REVIEW',timing:'RISK_ELEVATED',
    whyWrong:'OPEN',readiness:'REVIEW',evidenceFingerprint:'e1',thesisFingerprint:'t1',
    compoundSignal:'BUILDING_NEGATIVE',invalidatedConclusions:['c2','c1'],openGaps:['g2','g1'],nextTask:'Verify demand'
  };
  const b={...a};
  const sa=snapshot(a), sb=snapshot(b);

  ok('identical states produce identical fingerprint',sa.fingerprint===sb.fingerprint);
  ok('array order normalized for invalidated conclusions',sa.fingerprint===snapshot({...a,invalidatedConclusions:['c1','c2']}).fingerprint);
  ok('array order normalized for open gaps',sa.fingerprint===snapshot({...a,openGaps:['g1','g2']}).fingerprint);
  ok('key order does not change fingerprint',fingerprint({b:2,a:1})===fingerprint({a:1,b:2}));
  ok('changed readiness changes fingerprint',sa.fingerprint!==snapshot({...a,readiness:'BLOCKED'}).fingerprint);
  ok('changed evidence fingerprint changes snapshot',sa.fingerprint!==snapshot({...a,evidenceFingerprint:'e2'}).fingerprint);
  ok('changed thesis fingerprint changes snapshot',sa.fingerprint!==snapshot({...a,thesisFingerprint:'t2'}).fingerprint);
  ok('unchanged diff is not material',diff(sa,sb).material===false);

  const d=diff(sa,snapshot({...a,nextTask:'Check margin'}));
  ok('changed next task is detected',d.changes.includes('nextTask'));
  ok('changed next task is material',d.material===true);

  const d2=diff(sa,snapshot({...a,openGaps:['g1','g2','g3']}));
  ok('open gap change detected',d2.changes.includes('openGaps'));

  const d3=diff(sa,snapshot({...a,invalidatedConclusions:['c1']}));
  ok('invalidation change detected',d3.changes.includes('invalidatedConclusions'));

  ok('snapshot preserves symbol',sa.state.symbol==='AAA');
  ok('snapshot has deterministic version',sa.version===VERSION);

  return Object.freeze({passed:t.filter(x=>x.pass).length,total:t.length,tests:Object.freeze(t)});
}

const API={VERSION,stableValue,canonical,hashString,fingerprint,snapshot,diff,selfTest};
try{API.selfTestResult=selfTest()}catch(e){API.bootstrapError=String(e&&e.stack||e)}
global.ZooInvestResearchStateSnapshotEngine=Object.freeze(API);
})(engineContext);